import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faFileInvoiceDollar, faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ReportView } from '../../../../services/reports/reports.service';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { asCurrency } from '../../shared/utils/dashboard-page.utils';
import { PaymentByMethod, SectionKey } from '../../shared/types/dashboard-page.types';
import classes from './payments-section.module.scss';

export type PaymentsSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  paymentByMethod: PaymentByMethod;
  report: ReportView | null;
};

export const PaymentsSection: React.FC<PaymentsSectionProps> = ({
  collapsedSections,
  toggleSection,
  paymentByMethod,
  report,
}) => {
  const creditDetails = (report?.creditCardLines ?? [])
    .filter((line) => line.approved)
    .reduce<Map<string, { base: number; tip: number }>>((acc, line) => {
      const brand = line.cardBrand?.trim() || 'לא מזוהה';
      const current = acc.get(brand) ?? { base: 0, tip: 0 };
      acc.set(brand, { base: current.base + line.baseAmount, tip: current.tip + line.tipAmount });
      return acc;
    }, new Map());

  const creditDetailRows = [...creditDetails.entries()]
    .map(([brand, totals]) => ({ brand, base: totals.base, tip: totals.tip }))
    .filter((row) => row.base !== 0 || row.tip !== 0)
    .sort((a, b) => b.base - a.base);

  return (
    <SectionCard title="תשלומים" sectionKey="payments" collapsed={collapsedSections.payments} onToggle={() => toggleSection('payments')}>
      <div className={classes.grid}>
        <div className={classes.card}>
          <div className={classes.header}>
            <FontAwesomeIcon icon={faCreditCard as IconProp} />
            <span>אשראי</span>
          </div>
          <div className={classes.value}>{asCurrency(paymentByMethod.credit)}</div>
          {creditDetailRows.length > 0 ? (
            <>
              <div className={classes.cardDivider} />
              <div className={classes.detailsList}>
                {creditDetailRows.map((row) => (
                  <div key={row.brand} className={classes.detailsRow}>
                    <span className={classes.detailsAmount}>
                      {asCurrency(row.base)}
                      {row.tip !== 0 ? <span className={classes.detailsTip}>{`טיפ ${asCurrency(row.tip)}`}</span> : null}
                    </span>
                    <span className={classes.detailsBrand}>{row.brand}</span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
        <div className={classes.card}>
          <div className={classes.header}>
            <FontAwesomeIcon icon={faMoneyBillWave as IconProp} />
            <span>מזומן</span>
          </div>
          <div className={classes.value}>{asCurrency(paymentByMethod.cash)}</div>
        </div>
        <div className={classes.card}>
          <div className={classes.header}>
            <FontAwesomeIcon icon={faCreditCard as IconProp} />
            <span>תן ביס</span>
          </div>
          <div className={classes.value}>{asCurrency(paymentByMethod.tenbis)}</div>
        </div>
        <div className={classes.card}>
          <div className={classes.header}>
            <FontAwesomeIcon icon={faFileInvoiceDollar as IconProp} />
            <span>חשבון בית</span>
          </div>
          <div className={classes.value}>{asCurrency(paymentByMethod.houseAccount)}</div>
        </div>
      </div>
    </SectionCard>
  );
};
